import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

interface ReactivateMinisterDialogProps {
  open: boolean;
  ministerName: string;
  onConfirm: (eventDate: string, notes?: string) => Promise<void>;
  onCancel: () => void;
}

export function ReactivateMinisterDialog({
  open,
  ministerName,
  onConfirm,
  onCancel,
}: ReactivateMinisterDialogProps) {
  const todayIso = new Date().toISOString().split("T")[0];

  const [eventDate, setEventDate] = useState<string>(todayIso);
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(eventDate, notes.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      resetAndCancel();
    }
  };

  const resetAndCancel = () => {
    setEventDate(todayIso);
    setNotes("");
    onCancel();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Reactivar Ministro</AlertDialogTitle>
          <AlertDialogDescription>
            Reactivar a <span className="font-medium text-foreground">{ministerName}</span>.
            Esta acción queda registrada en el historial.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Date */}
          <div className="space-y-1.5">
            <Label htmlFor="reactivate-date">Fecha</Label>
            <Input
              id="reactivate-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              max={todayIso}
            />
          </div>

          {/* Notes (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="reactivate-notes">Notas (opcional)</Label>
            <Textarea
              id="reactivate-notes"
              placeholder="Observaciones adicionales..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetAndCancel} disabled={isSubmitting}>
            Cancelar
          </AlertDialogCancel>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Reactivar
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
